import { supabase } from '@/services/supabase/client';

export interface Announcement {
  id: string;
  content: string;
  link_text: string | null;
  link_url: string | null;
  type: 'info' | 'warning' | 'success';
}

export interface HomepageSection {
  id: string;
  key: string;
  title: string | null;
  subtitle: string | null;
  is_enabled: boolean;
  sort_order: number;
  layout: string;
  metadata: Record<string, unknown>;
}

export interface HomepageStats {
  industries: number;
  goals: number;
  problems: number;
  knowledge: number;
  workflows: number;
  products: number;
}

export async function getActiveAnnouncement(): Promise<Announcement | null> {
  const { data, error } = await supabase
    .from('announcements')
    .select('id, content, link_text, link_url, type')
    .eq('is_active', true)
    .order('sort_order')
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as Announcement | null;
}

export async function getHomepageSections(): Promise<HomepageSection[]> {
  const { data, error } = await supabase
    .from('homepage_sections')
    .select('*')
    .eq('is_enabled', true)
    .order('sort_order');

  if (error) throw error;
  return data as HomepageSection[];
}

export async function getHomepageStats(): Promise<HomepageStats> {
  const { data, error } = await supabase.rpc('get_homepage_stats');

  if (error) {
    const [industries, goals, problems, knowledge, workflows, products] = await Promise.all([
      supabase.from('industries').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('business_goals').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('business_problems').select('id', { count: 'exact', head: true }).eq('status', 'published'),
      supabase.from('knowledge_objects').select('id', { count: 'exact', head: true }).eq('status', 'published'),
      supabase.from('workflows').select('id', { count: 'exact', head: true }).eq('status', 'published'),
      supabase.from('products').select('id', { count: 'exact', head: true }).eq('is_active', true),
    ]);

    return {
      industries: industries.count ?? 0,
      goals: goals.count ?? 0,
      problems: problems.count ?? 0,
      knowledge: knowledge.count ?? 0,
      workflows: workflows.count ?? 0,
      products: products.count ?? 0,
    };
  }

  return data as HomepageStats;
}

export async function getFeaturedKnowledge() {
  const { data, error } = await supabase
    .from('knowledge_objects')
    .select('*')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(4);

  if (error) throw error;
  return data ?? [];
}

export async function getFeaturedWorkflows() {
  const { data, error } = await supabase
    .from('workflows')
    .select('*')
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(4);

  if (error) throw error;
  return data ?? [];
}

export async function getTrendingOpportunities() {
  const { data, error } = await supabase
    .from('business_problems')
    .select('*')
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(6);

  if (error) throw error;
  return data ?? [];
}
